import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    viewChild
} from "@angular/core";
import {SpinnerComponent} from "../../ui-elements/spinner/spinner.component";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {AbstractControl, NgControl, NgModel, ValidationErrors} from "@angular/forms";
import {FormComponent} from "../form/form.component";
import {ValidationMessage} from "@fedelta-pos/data-client";
import {FieldValidationMessage} from "./field-validation-message";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {LinkComponent} from '../../buttons/link/link.component';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-field",
    templateUrl: "./field.component.html",
    styleUrl: "./field.component.scss",
    host: {
        "[class.no-message]": "noMessage()"
    },
    imports: [
        LinkComponent,
        NgClass,
        SpinnerComponent,
        TouchableDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldComponent {
    private static nextId: number = 0;

    private formComponent: FormComponent = inject(FormComponent, {optional: true});
    private ngControl: NgControl = inject(NgControl, {optional: true});

    private readonly uid: number = FieldComponent.nextId++;

    /**
     * Stable per-instance ids so the projected native control can be wired up via aria-labelledby/aria-describedby -
     * namespaced by uid since the same validation key (e.g. "phoneNumber") can appear on more than one field on a page.
     */
    public readonly labelId: string = `woa-field-label-${this.uid}`;
    public readonly messageId: string = `woa-field-message-${this.uid}`;
    public readonly validationMessageId: string = `woa-field-validation-message-${this.uid}`;
    public readonly errorIdPrefix: string = `woa-field-error-${this.uid}`;

    public readonly label: InputSignal<string> = input.required<string>();

    public readonly link: InputSignal<string> = input<string>();
    public readonly message: InputSignal<string> = input<string>();
    public readonly validationMessage: InputSignal<string> = input<string>();
    public readonly translationParams: InputSignal<Record<string, string | number>> = input<Record<string, string | number>>({});
    public readonly noMessage: InputSignal<boolean> = input<boolean>(false);
    public readonly showMessageSpinner: InputSignal<boolean> = input<boolean>(false);
    public readonly required: InputSignal<boolean> = input<boolean>(false);
    public readonly readOnly: InputSignal<boolean> = input<boolean>(false);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly defaultInput: InputSignal<HTMLElement> = input<HTMLElement>();
    public readonly useContentModels: InputSignal<boolean> = input<boolean>(false);

    public onActivateField: OutputEmitterRef<void> = output<void>();
    public onActivateLink: OutputEmitterRef<void> = output<void>();

    public touchable: Signal<TouchableDirective> = viewChild(TouchableDirective);
    private models: Signal<ReadonlyArray<NgModel>> = contentChildren(NgModel, {descendants: true});

    public hasError: Signal<boolean> = computed(() => {
        if (!this.formComponent) {
            return false;
        }
        if (!this.useContentModels() && this.ngControl) {
            return this.formComponent.hasError()(this.ngControl.control);
        }
        if (this.useContentModels() && this.models()) {
            return !!this.models().find((model: NgModel) => this.formComponent.hasError()(model.control));
        }
        return false;
    });

    public showMessages: Signal<boolean> = computed(() => !this.noMessage() && this.showMessageSpinner() || !!this.message() || !!this.validationMessage() || this.hasError());

    public isRequired: Signal<boolean> = computed(() => {
        if (this.required()) {
            return true;
        }
        if (!this.useContentModels() && this.ngControl && this.ngControl.validator) {
            const validationErrors: ValidationErrors = this.ngControl.validator({} as AbstractControl);
            return validationErrors && validationErrors["required"];
        }
        if (this.useContentModels() && this.models()) {
            return !!this.models().find((model: NgModel) => {
                const validationErrors: ValidationErrors = model.validator({} as AbstractControl);
                return validationErrors && validationErrors["required"];
            });
        }
        return false;
    });

    public fieldValidationMessages: Signal<FieldValidationMessage[]> = computed(() => {
        if (!this.hasError()) {
            return [];
        }
        if (!this.useContentModels() && this.ngControl) {
            return Object.keys(this.ngControl.control.errors)
                .map((key: string) => this.mapStandardError(this.ngControl.control.errors, key))
                .filter((error: FieldValidationMessage) => error.message);
        }
        if (this.useContentModels() && this.models()) {
            let messages: FieldValidationMessage[] = [];
            this.models().filter((model: NgModel) => !!model.control.errors)
                .forEach((model: NgModel) => Object.keys(model.control.errors)
                    .map((key: string) => this.mapStandardError(model.control.errors, key))
                    .filter((message: FieldValidationMessage) => !messages.find((existing: FieldValidationMessage) => message.validationName === existing.validationName))
                    .forEach((message: FieldValidationMessage) => messages.push(message)));
            return messages;
        }
        return [];
    });

    public serverValidationMessages: Signal<ValidationMessage[]> = computed(() => this.formComponent && this.ngControl && this.formComponent.hasError()(this.ngControl.control, "server") ? this.ngControl.control.errors["server"]?.validationMessages : undefined);

    /**
     * Ids of every message currently displayed for this field, for the projected native control's aria-describedby.
     */
    public readonly describedBy: Signal<string | undefined> = computed(() => {
        const ids: string[] = [];
        if (this.message()) {
            ids.push(this.messageId);
        }
        if (this.validationMessage()) {
            ids.push(this.validationMessageId);
        }
        this.fieldValidationMessages().forEach((message: FieldValidationMessage) => ids.push(`${this.errorIdPrefix}-${message.validationName}`));
        (this.serverValidationMessages() ?? []).forEach((_: ValidationMessage, index: number) => ids.push(`${this.errorIdPrefix}-server-${index}`));
        return ids.length ? ids.join(" ") : undefined;
    });

    /**
     * Handle standard angular validations (convert to FieldValidationMessage)
     */
    private mapStandardError(errors: ValidationErrors, key: string): FieldValidationMessage {
        return key === "required" ? new FieldValidationMessage(key, "woa.validation.common.required", errors[key])
            : key === "minlength" ? new FieldValidationMessage(key, "woa.validation.common.minLength", errors[key])
                : key === "maxlength" ? new FieldValidationMessage(key, "woa.validation.common.maxLength", errors[key])
                    : errors[key];
    }

    activateField(event: MouseEvent): void {
        if ((event.target as HTMLElement).tagName !== "INPUT" && this.defaultInput()) {
            this.defaultInput().focus();
        }
        this.onActivateField.emit();
    }
}
